/**
 * SYNCED FROM SERVER — do not edit by hand (mirror of
 * `server/src/domain/schemas/shared/list-filters.schema.ts`; keep the two in
 * lockstep manually — do NOT run sync:contracts).
 *
 * Shared operator-envelope schemas for list/grid filtering — the wire model
 * behind the web app's cubby-style filter pills (field | operator | value).
 * Endpoints widen legacy value-only filter fields with
 * `z.union([legacyScalar, envelope])`; the server normalizes scalars into
 * envelopes before translating to Prisma.
 */

import { z } from "zod";

// select --------------------------------------------------------------------

export const SELECT_FILTER_OPS = ["is", "isNot", "blank", "notBlank"] as const;
export type SelectFilterOp = (typeof SELECT_FILTER_OPS)[number];

export function selectFilterSchema<T extends z.ZodTypeAny>(value: T) {
	return z.object({
		op: z.enum(SELECT_FILTER_OPS),
		value: value.optional(),
	});
}
export const SelectFilterSchema = selectFilterSchema(
	z.string().trim().max(120)
);
export type SelectFilter = z.infer<typeof SelectFilterSchema>;

// multiselect ---------------------------------------------------------------

export const MULTISELECT_FILTER_OPS = [
	"isAnyOf",
	"isNotAnyOf",
	"includesAll",
	"blank",
] as const;
export type MultiSelectFilterOp = (typeof MULTISELECT_FILTER_OPS)[number];

const csvValues = <T extends z.ZodTypeAny>(item: T) =>
	z.preprocess(
		(value) =>
			typeof value === "string" ? value.split(",").filter(Boolean) : value,
		z.array(item).max(50)
	);

export function multiSelectFilterSchema<T extends z.ZodTypeAny>(item: T) {
	return z.object({
		op: z.enum(MULTISELECT_FILTER_OPS),
		values: csvValues(item).optional(),
	});
}
export const MultiSelectFilterSchema = multiSelectFilterSchema(
	z.string().trim().max(120)
);
export type MultiSelectFilter = z.infer<typeof MultiSelectFilterSchema>;

// number --------------------------------------------------------------------

export const NUMBER_FILTER_OPS = ["eq", "neq", "gt", "lt", "between"] as const;
export type NumberFilterOp = (typeof NUMBER_FILTER_OPS)[number];

export const NumberFilterSchema = z.object({
	op: z.enum(NUMBER_FILTER_OPS),
	value: z.coerce.number().optional(),
	valueTo: z.coerce.number().optional(),
});
export type NumberFilter = z.infer<typeof NumberFilterSchema>;

// date ----------------------------------------------------------------------

export const DATE_FILTER_OPS = [
	"before",
	"after",
	"between",
	"blank",
	"notBlank",
] as const;
export type DateFilterOp = (typeof DATE_FILTER_OPS)[number];

export const DateFilterSchema = z.object({
	op: z.enum(DATE_FILTER_OPS),
	/** Operand for before/after; lower bound for between. ISO datetime. */
	from: z.string().datetime({ offset: true }).optional(),
	/** Upper bound for between. ISO datetime. */
	to: z.string().datetime({ offset: true }).optional(),
});
export type DateFilter = z.infer<typeof DateFilterSchema>;

/**
 * Legacy `xxxFrom`/`xxxTo` ISO pair → a `between` envelope. Open-ended pairs
 * stay `between` with a missing bound (server emits gte/lte only), preserving
 * legacy inclusive semantics — `before`/`after` are strict (lt/gt).
 */
export function dateFilterFromBounds(
	from?: string,
	to?: string
): DateFilter | undefined {
	if (!(from || to)) {
		return undefined;
	}
	return { from, op: "between", to };
}
